/**
 * Czysta fizyka button-mash wyścigu pływackiego (§14.5).
 * CLIENT cap 12 tapów/s — nadmiar ignorowany; serwer i tak clampuje (§7.2).
 */
import { MAX_TAPS_PER_S } from '@pieskowo/shared';

export const CLIENT_MAX_TAPS_PER_S = MAX_TAPS_PER_S; // 12
export const VELOCITY_DAMP = 0.95;
export const BASE_IMPULSE = 1.0;

/** Style pływania — kosmetyczne modyfikatory (żaden nie jest „zły"). */
export type SwimStyleId = 'piesek' | 'zabka' | 'motylek';

export type SwimStyleDef = {
  id: SwimStyleId;
  labelPl: string;
  emoji: string;
  /** Mnożnik impulsu (motylek = +zryw). */
  impulseMul: number;
  /** Mnożnik tłumienia bliższy 1 = +stabilność (żabka). */
  dampMul: number;
};

export const SWIM_STYLES: readonly SwimStyleDef[] = [
  {
    id: 'piesek',
    labelPl: 'Piesek',
    emoji: '🐾',
    impulseMul: 1.0,
    dampMul: 1.0,
  },
  {
    id: 'zabka',
    labelPl: 'Żabka',
    emoji: '🐸',
    impulseMul: 0.95,
    dampMul: 1.02, // wolniejsze gaszenie → stabilniej
  },
  {
    id: 'motylek',
    labelPl: 'Motylek',
    emoji: '🦋',
    impulseMul: 1.12, // +zryw
    dampMul: 0.98,
  },
] as const;

export function styleById(id: SwimStyleId): SwimStyleDef {
  return SWIM_STYLES.find((s) => s.id === id) ?? SWIM_STYLES[0]!;
}

/**
 * Czy klient może przyjąć kolejny tap przy limicie 12/s.
 * `windowStartMs` = początek bieżącego okna 1 s; `tapsInWindow` w tym oknie.
 */
export function canAcceptTap(
  nowMs: number,
  windowStartMs: number,
  tapsInWindow: number,
  maxPerS: number = CLIENT_MAX_TAPS_PER_S,
): { accept: boolean; windowStartMs: number; tapsInWindow: number } {
  let start = windowStartMs;
  let count = tapsInWindow;
  if (nowMs - start >= 1000) {
    start = nowMs;
    count = 0;
  }
  if (count >= maxPerS) {
    return { accept: false, windowStartMs: start, tapsInWindow: count };
  }
  return { accept: true, windowStartMs: start, tapsInWindow: count + 1 };
}

/** Impuls na tap: v += impulse * style.impulseMul */
export function applyImpulse(
  velocity: number,
  style: SwimStyleDef,
  baseImpulse: number = BASE_IMPULSE,
): number {
  return velocity + baseImpulse * style.impulseMul;
}

/**
 * Tłumienie na tick: v *= 0.95 * style.dampMul (clamp ≤ 0.999 żeby nie rosło).
 * Tick = klatka logiki (~60 Hz w scenie).
 */
export function dampVelocity(velocity: number, style: SwimStyleDef): number {
  const factor = Math.min(0.999, VELOCITY_DAMP * style.dampMul);
  return velocity * factor;
}

/** Przesunięcie pozycji wzdłuż toru (jednostki „metry" umowne). */
export function advancePosition(pos: number, velocity: number, dtSec: number): number {
  return pos + Math.max(0, velocity) * Math.max(0, dtSec) * 10;
}

/**
 * Gumowanie NPC: dociąga postęp do gracza, nigdy nie odpływa daleko.
 * `npcPos` dąży do zakresu [player - lag, player + lead].
 */
export function rubberBandNpc(
  npcPos: number,
  playerPos: number,
  opts: { maxLag?: number; maxLead?: number; pull?: number } = {},
): number {
  const maxLag = opts.maxLag ?? 8;
  const maxLead = opts.maxLead ?? 4;
  const pull = opts.pull ?? 0.08;
  const min = playerPos - maxLag;
  const max = playerPos + maxLead;
  let p = npcPos;
  if (p < min) p += (min - p) * Math.min(1, pull * 3);
  if (p > max) p -= (p - max) * Math.min(1, pull * 3);
  // delikatne dociąganie do gracza (łagodna krzywa)
  p += (playerPos - p) * pull * 0.15;
  return p;
}
